import { Dispatch, SetStateAction } from "react";
import { Segment } from "./prompt-types";
import type { PromptOptions } from "@/components/playground/prompt-caption";
import { InferenceType, SegmentResult } from "@/lib/playground-utils";

type StreamMultiSegmentationArgs = {
  inferenceUrl: string;
  query: string;
  imageFile: File;
  uploadedImage: string;
  promptOptions: PromptOptions;
  reasoningEnabled: boolean;
  variant?: string;
  setInferences: Dispatch<SetStateAction<InferenceType[]>>;
};

type Point = { x: number; y: number };

const INITIAL_MESSAGE = "Running point detection...";
const SEGMENT_STREAM_ERROR = "Segment streaming failed.";

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const toPointArray = (raw: unknown): Point[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p) => {
      if (p && typeof p === "object" && "x" in p && "y" in p) {
        const { x, y } = p as { x: unknown; y: unknown };
        if (typeof x === "number" && typeof y === "number") {
          return { x, y };
        }
      }
      return null;
    })
    .filter((p): p is Point => Boolean(p));
};

// Performs a non-streaming /point call to retrieve points
async function callPoint(
  inferenceUrl: string,
  prompt: string,
  base64Image: string,
  variant?: string,
): Promise<Point[]> {
  const body: Record<string, unknown> = {
    image_url: base64Image,
    object: prompt || "all objects",
  };
  if (variant) body.variant = variant;

  const resp = await fetch(`${inferenceUrl}/point`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`Point API error: ${resp.status} ${resp.statusText}`);
  const json = await resp.json();
  return toPointArray(json.points ?? json.point ?? json);
}

// Streams a single /segment with spatial_refs for one point
async function streamSegmentForPoint(
  inferenceUrl: string,
  prompt: string,
  base64Image: string,
  variant: string | undefined,
  point: Point,
  onUpdate: (snapshot: SegmentResult, status: InferenceType["status"]) => void,
  onDone: (snapshot: SegmentResult, duration: number) => void,
): Promise<void> {
  const requestBody: Record<string, unknown> = {
    image_url: base64Image,
    object: prompt,
    spatial_refs: [[point.x, point.y]],
    stream: true,
  };
  if (variant) requestBody.variant = variant;

  const requestInit: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  };

  const response = await fetch(`${inferenceUrl}/segment`, requestInit);
  if (!response.ok) throw new Error(`Segment API error: ${response.status} ${response.statusText}`);
  const reader = response.body?.getReader();
  if (!reader) throw new Error("Streaming not supported for segment response.");

  const startTime = performance.now();
  const decoder = new TextDecoder();
  let buffer = "";

  let bbox: SegmentResult["bbox"] | null = null;
  let path = "";

  const emit = (status: InferenceType["status"]) => {
    if (!bbox) return;
    const snapshot: SegmentResult = { bbox, path: path.trim() };
    onUpdate(snapshot, status);
  };

  const processLine = (line: string) => {
    const trimmed = line.trim();
    if (trimmed === "" || trimmed.startsWith(":")) return;
    if (!trimmed.startsWith("data:")) return;
    const payload = trimmed.slice(5).trim();
    if (payload === "" || payload === "[DONE]") return;
    let obj: unknown;
    try {
      obj = JSON.parse(payload);
    } catch (err) {
      console.error("Failed to parse segment payload", err);
      return;
    }
    if (!obj || typeof obj !== "object") return;
    const rec = obj as Record<string, unknown>;
    const type = typeof rec.type === "string" ? rec.type : undefined;
    if (type === "bbox") {
      const b = rec.bbox as Record<string, unknown>;
      if (
        b &&
        ["x_min", "y_min", "x_max", "y_max"].every((k) => typeof b[k] === "number")
      ) {
        bbox = {
          x_min: b.x_min as number,
          y_min: b.y_min as number,
          x_max: b.x_max as number,
          y_max: b.y_max as number,
        };
        emit("streaming");
      }
      return;
    }
    if (type === "path_delta") {
      const chunk = typeof rec.chunk === "string" ? rec.chunk : "";
      if (chunk) {
        path = path ? `${path}${chunk}` : chunk;
        emit("streaming");
      }
      return;
    }
    if (type === "final") {
      const finalPath = typeof rec.path === "string" ? rec.path : path;
      if (finalPath) path = finalPath;
      const b = rec.bbox as Record<string, unknown> | undefined;
      if (
        b &&
        ["x_min", "y_min", "x_max", "y_max"].every((k) => typeof b[k] === "number")
      ) {
        bbox = {
          x_min: b.x_min as number,
          y_min: b.y_min as number,
          x_max: b.x_max as number,
          y_max: b.y_max as number,
        };
      }
      if (bbox) {
        const snapshot: SegmentResult = { bbox, path: path.trim() };
        onDone(snapshot, performance.now() - startTime);
      }
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      buffer += decoder.decode();
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n")) !== -1) {
      const line = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 1);
      processLine(line);
    }
  }
  if (buffer.trim().length > 0) {
    processLine(buffer);
  }
}

export async function streamSegmentationMultiInference(args: StreamMultiSegmentationArgs) {
  const { inferenceUrl, query, imageFile, uploadedImage, variant, setInferences } = args;
  const inferenceId = Date.now();

  // Precompute image once for all downstream requests.
  const base64Image = await fileToBase64(imageFile);

  setInferences((prev) => [
    {
      id: inferenceId,
      image: uploadedImage,
      imageFile,
      result: INITIAL_MESSAGE,
      capability: Segment,
      query,
      status: "pending",
      segmentResults: [],
    },
    ...prev,
  ]);

  const update = (partial: Partial<InferenceType>) =>
    setInferences((prev) =>
      prev.map((item) => (item.id === inferenceId ? { ...item, ...partial } : item)),
    );

  try {
    const points = await callPoint(inferenceUrl, query, base64Image, variant);
    if (!points.length) {
      update({
        result: "No points returned from Point inference.",
        status: "done",
        duration: 0,
      });
      return;
    }

    const collected: Array<SegmentResult | null> = new Array(points.length).fill(null);

    update({
      result: `Received ${points.length} point(s), streaming segments...`,
      status: "streaming",
      segmentResults: [...collected],
    });

    await Promise.all(
      points.map((p, idx) =>
        streamSegmentForPoint(
          inferenceUrl,
          query,
          base64Image,
          variant,
          p,
          (snapshot, status) => {
            collected[idx] = snapshot;
            const next = [...collected];
            update({
              result: JSON.stringify(next, null, 2),
              segmentResults: next,
              status,
            });
          },
          (snapshot, duration) => {
            collected[idx] = snapshot;
            const next = [...collected];
            update({
              result: JSON.stringify(next, null, 2),
              segmentResults: next,
              status: "streaming",
              duration,
            });
          },
        ),
      ),
    );

    update({
      status: "done",
      result: JSON.stringify([...collected], null, 2),
      segmentResults: [...collected],
    });
  } catch (error) {
    console.error("Segment streaming error", error);
    update({ result: SEGMENT_STREAM_ERROR, status: "error" });
    throw error;
  }
}
